import { type AssetRef } from '@/platform/learning/contract'

import { type ChoiceItem, selectionLimits } from './choice'
import { MockBadge } from './MockBadge'

/**
 * EN-CHOICE renderer (Exercise Catalog §4.1): text choice — single, multiple
 * or true/false — and single-select picture choice. It only collects option
 * IDs; correctness comes from the learning service (FR-50), so nothing here
 * knows or marks the right answer.
 *
 * Options are native radio buttons (single) or checkboxes (multiple) inside
 * large card labels, so keyboard behaviour (one Tab stop and arrow keys for a
 * radio group, Space to toggle) and the checked/disabled state come from the
 * browser rather than custom ARIA.
 */

export interface ChoiceActivityProps {
  readonly item: ChoiceItem
  readonly selected: readonly string[]
  /** True once the answer is being checked or was checked: no more changes. */
  readonly locked: boolean
  readonly onChange: (selected: string[]) => void
}

/**
 * A picture option. With a file, only the picture is shown and `alt_text` is
 * its accessible name — a visible caption would give the answer away. Without
 * a file (FR-144) a placeholder shows the description instead, with a MOCK
 * label for mock assets (decision D-05).
 */
function ImageOption({ image }: { image: AssetRef }): React.JSX.Element {
  if (image.url !== null) {
    return (
      <img
        src={image.url}
        alt={image.alt_text}
        className="aspect-[4/3] w-full rounded-xl object-cover"
      />
    )
  }
  return (
    <span className="flex w-full flex-col items-center gap-2">
      <span
        className="flex aspect-[4/3] w-full items-center justify-center rounded-xl bg-slate-200 text-4xl dark:bg-slate-800"
        aria-hidden
      >
        🖼️
      </span>
      <span className="text-center text-base">{image.alt_text}</span>
      {image.is_mock && <MockBadge decorative={/\bMOCK\b/.test(image.alt_text)} />}
    </span>
  )
}

function optionClass(selected: boolean, picture: boolean): string {
  return `relative flex min-h-14 w-full cursor-pointer items-center gap-3 rounded-2xl border-2 text-left text-lg font-medium wrap-break-word transition-colors has-[:disabled]:cursor-default has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-sky-600 dark:has-[:focus-visible]:outline-sky-400 ${
    picture ? 'flex-col justify-center p-2' : 'px-4 py-3'
  } ${
    selected
      ? 'border-sky-700 bg-sky-50 text-sky-950 dark:border-sky-400 dark:bg-sky-950 dark:text-sky-50'
      : 'border-slate-300 bg-white text-slate-900 hover:border-sky-500 has-[:disabled]:hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:has-[:disabled]:hover:border-slate-700'
  }`
}

export function ChoiceActivity({
  item,
  selected,
  locked,
  onChange,
}: ChoiceActivityProps): React.JSX.Element {
  const { max } = selectionLimits(item)
  const single = max === 1
  const picture = item.activity_type === 'picture_choice'

  const choose = (optionId: string) => {
    if (locked) return
    if (single) {
      onChange([optionId])
      return
    }
    if (selected.includes(optionId)) onChange(selected.filter((o) => o !== optionId))
    else if (selected.length < max) onChange([...selected, optionId])
  }

  const options =
    item.activity_type === 'text_choice'
      ? item.options.map((o) => ({ id: o.option_id, content: <span>{o.text}</span> }))
      : item.options.map((o) => ({
          id: o.option_id,
          content: <ImageOption image={o.image} />,
        }))

  return (
    <div className="flex flex-col gap-4">
      {item.activity_type === 'text_choice' && item.context !== null && (
        <p className="rounded-xl bg-white p-4 text-lg wrap-break-word dark:bg-slate-900">
          {item.context}
        </p>
      )}
      <fieldset className="flex min-w-0 flex-col gap-4">
        <legend className="mb-4 text-2xl font-semibold wrap-break-word">
          {item.prompt}
        </legend>
        {item.activity_type === 'picture_choice' && item.prompt_audio !== null && (
          <p className="text-base text-slate-700 dark:text-slate-300">
            <span aria-hidden>🔈 </span>
            {item.prompt_audio.alt_text}
            {item.prompt_audio.is_mock && (
              <>
                {' '}
                <MockBadge decorative={/\bMOCK\b/.test(item.prompt_audio.alt_text)} />
              </>
            )}
          </p>
        )}
        {!single && (
          <p className="text-base text-slate-700 dark:text-slate-300">
            Chọn tối đa {String(max)} đáp án.
          </p>
        )}
        <div
          className={
            picture ? 'grid grid-cols-2 gap-3 md:grid-cols-3' : 'flex flex-col gap-3'
          }
        >
          {options.map(({ id, content }) => {
            const isSelected = selected.includes(id)
            const blocked = locked || (!single && !isSelected && selected.length >= max)
            return (
              <label key={id} className={optionClass(isSelected, picture)}>
                <input
                  type={single ? 'radio' : 'checkbox'}
                  name={`choice-${item.item_id}`}
                  value={id}
                  checked={isSelected}
                  disabled={blocked}
                  onChange={() => {
                    choose(id)
                  }}
                  className="sr-only"
                />
                {content}
                {isSelected && (
                  <span
                    className={
                      picture
                        ? 'absolute top-3 right-3 flex size-8 items-center justify-center rounded-full bg-sky-700 text-lg text-white shadow dark:bg-sky-400 dark:text-slate-950'
                        : 'ml-auto text-xl'
                    }
                    aria-hidden
                  >
                    ✔
                  </span>
                )}
              </label>
            )
          })}
        </div>
      </fieldset>
    </div>
  )
}
